/**
 * @module client-api-base
 * @module_directory /modules/client/base
 * @module_path ui/errors
 * @description Error boundary for the application
 *
 * @todo Check the styling of the Error Boundary
 *
 */
import { Button } from "@material-ui/core";
import HomeIcon from "@material-ui/icons/Home";
import { useAuth } from "@qpr/client-library";
import clsx from "clsx";
import { useAppContext } from "./../../context/AppCtxProvider";
import { makeThemedStyles } from "./../../viewcomponents/utils";
import { t } from "i18next";
import React from "react";
import { Link, useParams } from "react-router-dom";
const useStyles = makeThemedStyles("ErrorBoundary", (theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background:
      "linear-gradient(226.09deg, #DCE7E9 -5.75%, rgba(255, 255, 255, 0) 74.52%), linear-gradient(220.4deg, #C3D7DA 14.33%, #E3E1EA 49.58%, #D5E6E7 78.35%)",
    height: "100vh",
    width: "100vw",
    position: "relative",
  },
  link: {
    lineHeight: 1,
    fontSize: "1.6em",
  },
  message: {
    fontSize: "3rem",
  },
  logout: {
    fontSize: "1.82em",
    position: "absolute",
    top: "1.25em",
    right: "3.12em",
    textDecoration: "underline",
    textTransform: "unset",
  },
  logoutRtl: {
    right: "unset",
    left: "3.12em",
  },
  icon: {
    marginLeft: "8px",
    marginRight: "-4px",
  },
}));
type IState = {
  error: {
    message: string;
    status?: number;
  };
  errorInfo: {
    componentStack: string;
  } | null;
};
const ErrorWithStatus = (props: { message: string; status?: number }) => {
  const { message, status } = props;
  const { tenantSymbol } = useParams<{
    tenantSymbol: string;
  }>();
  const classes = useStyles();
  const { logout } = useAuth();
  const { model, rtl } = useAppContext();
  return (
    <div className={classes.root}>
      <Button
        size="large"
        className={clsx(classes.logout, rtl && classes.logoutRtl)}
        variant="text"
        onClick={logout}
      >
        {t("Logout")}
      </Button>
      <h1 className={classes.message}>{message}</h1>
      <Button
        component={Link}
        classes={{
          root: classes.link,
          startIcon: rtl ? classes.icon : undefined,
        }}
        to={`/${tenantSymbol}/${model}/landingpage`}
        startIcon={<HomeIcon />}
        variant="contained"
        color="primary"
        size="large"
      >
        {t("Home")}
      </Button>
    </div>
  );
};
class ErrorBoundary extends React.Component<{}, IState> {
  constructor(props: any) {
    super(props);
    this.state = { error: { message: "" }, errorInfo: null };
  }
  componentDidCatch(
    error: {
      message: string;
    },
    errorInfo: {
      componentStack: string;
    }
  ) {
    try {
      // Catch errors in any components below and re-render with error message
      this.setState({
        error: JSON.parse(error.message),
        errorInfo: errorInfo,
      });
    } catch (error) {
      this.setState({
        error: { message: "Something went wrong!" },
        errorInfo: errorInfo,
      });
    }
    // You can also log error messages to an error reporting service here
  }
  render() {
    if (this.state.errorInfo) {
      if (this.state.error.status)
        return <ErrorWithStatus {...this.state.error} />;
      return (
        <div style={{ marginTop: "70px", width: "100%", textAlign: "center" }}>
          <h2>Something went wrong.</h2>
          <details style={{ whiteSpace: "pre-wrap" }}>
            {this.state.error && this.state.error.toString()}
            <br />
            {this.state.errorInfo.componentStack}
          </details>
        </div>
      );
    }
    // Normally, just render children
    return this.props.children;
  }
}
export default ErrorBoundary;
